import React, { useState, useEffect } from "react"
import Flink from '~/components/shared/flink'
import { useTheme } from '~/contexts/ThemeContext'
import { usePost } from '~/contexts/PostContext'
import { jnstyls, notBlank, imgSize } from '~/utils/helpers'

import QuickCaption from './quick-caption'
import { ImgTitle } from '~/components/home/contents'
import VideoNoControl from '~/components/video/no-controls'

import * as styles from "~/styles/blog/blog-hero.module.scss"
import * as bstyle from "~/styles/blog/blog.module.scss"


const BlogHero = ({ blockId, index, pdp=false }) => {
  const { isMobile } = useTheme()
  const { post, props } = usePost()
  const { quickProds } = props
  const blogTitle = post.title
  const block = post.data.blocks.find(x => x.id === blockId)
  const { settings } = block

  const [didLoad, setDidLoad] = useState(false)

  let classes = [styles['hero_a_' + settings.align], styles['hero_ma_' + settings.mobile_align]]
  if (settings.padding) classes.push(styles.hero_pad)
  if (!!settings.stretch && settings.stretch) classes.push(styles.wide)
  if (pdp) classes.push(styles.pdp)
  if (index === 0) classes.push(bstyle.first)
  classes = classes.filter(x => !!x).length ? classes.join(' ') : ''
  
  const has_over = notBlank(settings.ov_title) || notBlank(settings.ov_copy)
  let pdp_link = settings.link1 || settings.cta_link || settings.cta_link_right
  let hero_link = (pdp && pdp_link) || false
  if (!(pdp && has_over) && notBlank(settings.img_link)) hero_link = settings.img_link
  if (pdp && !pdp_link && hero_link) pdp_link = hero_link

  const home_caption = notBlank(settings.title) || notBlank(settings.subtitle) || notBlank(settings.copy) || notBlank(settings.cta)

  const desk_align = (settings.overlay_align && settings.overlay_align !== 'left') ? settings.overlay_align : false
  const mobi_align = (settings.mob_overlay_align && settings.mob_overlay_align !== 'left') ? settings.mob_overlay_align : false

  let capt_font = settings.capt_font || ''
  if (notBlank(capt_font)) capt_font = styles[`use_${capt_font}`]

  let overlay_font = settings.overlay_font || ''
  if (notBlank(overlay_font)) overlay_font = styles[`use_${overlay_font}`]

  let page_styles = ''
  if (parseInt(settings.scale) < 100 || parseInt(settings.mobile_scale) < 100) {
    if (parseInt(settings.scale) < 100 ) {
      page_styles += `
        @media only screen and (min-width: 800px) {
          .image${block.id} {
            max-width: ${settings.scale}%;
            margin-left: auto;
            margin-right: auto;
          }
          .caption${block.id} {
            margin-left: auto;
            margin-right: auto;
            max-width: ${settings.scale}%;
          }
        }`
    }
    if (parseInt(settings.mobile_scale) < 100) {
      page_styles += `
        @media only screen and (max-width: 800px) {
          .image${block.id} {
            max-width: ${settings.mobile_scale}%;
          }
          .caption${block.id} {
            margin-left: auto;
            margin-right: auto;
            max-width: ${settings.mobile_scale}%;
          }
        }`
    }
  }
  if (blockId === 2008) {
    page_styles += `
      @media only screen and (min-width: 820px) {
        [block="2008"] .${bstyle.title}.${styles.title} {
          font-size: 6.1rem;
        }
      }
      @media only screen and (max-width: 819px) {
        [block="2008"] .${bstyle.title}.${styles.title} {
          font-size: 6.1rem;
        }
      }`
  }
  if (settings.caption_align !=='left') {
    page_styles += `
      @media only screen and (min-width: 800px) {
        .caption${block.id} {
          text-align: ${settings.caption_align};
        }
      }`
  }
  if (desk_align) {
    page_styles += `
      @media only screen and (min-width: 800px) {
        .image${block.id} .${styles.overlay} {
          align-items: ${desk_align === 'right' ? 'flex-end' : desk_align};
          display: flex;
          flex-direction: column;
          justify-content: center;
          text-align: ${desk_align};
        }
      }`
  }
  if (mobi_align) {
    page_styles += `
      @media only screen and (max-width: 800px) {
        .image${block.id} .${styles.overlay} {
          align-items: ${mobi_align === 'right' ? 'flex-end' : mobi_align};
          display: flex;
          flex-direction: column;
          justify-content: center;
          text-align: ${mobi_align};
        }
      }`
  }
  if (notBlank(settings.text_color) ) {
    page_styles += `
      @media only screen and (min-width: 800px) {
        .image${block.id} .${styles.overlay} {
          color: ${settings.text_color};
        }
      }`
  }
  if (notBlank(settings.mobi_text_color)) {
    page_styles += `
      @media only screen and (max-width: 800px) {
        .image${block.id} .${styles.overlay} {
          color: ${settings.mobi_text_color};
        }
      }`
  }
  const desk_ratio = block?.desk_size?.ratio || false
  const mobi_ratio = block?.mobi_size?.ratio || false
  if (desk_ratio) {
    page_styles += `
      @media only screen and (min-width: 820px) {
        [block="${block.id}"] [bimg] {
          aspect-ratio: ${desk_ratio} auto;
        }
      }
    `
  }
  if (mobi_ratio) {
    page_styles += `
      @media only screen and (max-width: 819px) {
        [block="${block.id}"] [bimg] {
          aspect-ratio: ${mobi_ratio} auto;
        }
      }
    `
  }
  
  useEffect(() => {
    setDidLoad(true)
  }, [])

  return (
    <div 
      className={jnstyls([bstyle.sec, styles.hero, classes])}
      block={block.id}
    > 
      {notBlank(page_styles) && (<style dangerouslySetInnerHTML={{ __html: page_styles}} />)}
      
      <div className={`${styles.image} image${block.id}`} bimg={''}>
        {notBlank(settings.img_stamp) && (
          <img className={styles.hero_stamp} alt={'decorative stamp'} src={possible_png(settings.img_stamp)} height={100} width={100} />
        )}
        
        {hero_link ? (
          <Flink href={hero_link}>
            <HeroImg isMobile={isMobile} settings={settings} blogTitle={blogTitle} />
          </Flink>
        ) : (
          <HeroImg isMobile={isMobile} settings={settings} blogTitle={blogTitle} />
        )}

        {has_over && (
          <div className={jnstyls([styles.overlay, overlay_font])}>
            {notBlank(settings.ov_cta_link) ? (
              <Flink className={styles.title_link} href={settings.ov_cta_link}>
                <>
                  {!settings.img_title ? (
                    <>
                      {index === 0 ? (
                        <h1 
                          className={jnstyls([bstyle.title, styles.title])}
                          dangerouslySetInnerHTML={{ __html: settings.ov_title }}
                        />
                      ) : (
                        <div 
                          className={jnstyls([bstyle.title, styles.title])} 
                          dangerouslySetInnerHTML={{ __html: settings.ov_title }}
                        />
                      )}
                    </>
                  ) : (
                    <ImgTitle 
                      img_title={settings.img_title} 
                      desk_height={settings.img_title_dheight}
                      mobi_height={settings.img_title_mheight}
                      title={{copy: settings.ov_title ? settings.ov_title : '', url: ''}}
                      klass={`image${block.id}`}
                    />
                  )}
                </>
              </Flink>
            ) : (
              <div 
                className={jnstyls([bstyle.title, styles.title])} 
                dangerouslySetInnerHTML={{ __html: settings.ov_title }}
              />
            )}
            {notBlank(settings.ov_copy) && (
              <div className={styles.copy_body} dangerouslySetInnerHTML={{ __html: settings.ov_copy }} />
            )}
            {notBlank(settings.ov_cta_link) && (
              <div className={styles.copy_cta}>
                <Flink
                  className={bstyle.cta_btn} 
                  href={settings.ov_cta_link} 
                  dangerouslySetInnerHTML={{ __html: settings.ov_cta }} 
                />
              </div>
            )}
          </div>
        )}

        {notBlank(settings.credit) && (
          <div className={bstyle.credit}>{settings.credit}</div>
        )}
      </div>

      {(notBlank(settings.caption) || notBlank(settings.link1) || notBlank(settings.link2)) && (
        <div className={jnstyls([styles.caption, 'caption' + block.id, props?.pgblc ? styles.pg_capt : ''])}>
          {notBlank(settings.caption) ? (
            <>
              {(didLoad && settings.caption?.includes('/products/') && quickProds.length > 0) ? (
                <QuickCaption 
                  copy={settings.caption} 
                  styles={''}
                />
              ) : (
                <div dangerouslySetInnerHTML={{ __html: settings.caption }} />
              )}
            </>
          ) : (
            <>
              {notBlank(settings.link1) && (
                <Flink href={settings.link1} className={bstyle.cta_link}>{settings.link1_copy}</Flink>
              )}
              {notBlank(settings.link2) && (
                <Flink href={settings.link2} className={bstyle.cta_link}>{settings.link2_copy}</Flink>
              )}
            </>
          )}
        </div>
      )}

      {home_caption && (
        <div className={jnstyls([styles.hcaption, capt_font, props?.pgblc ? styles.pg_capt : ''])}>
          {settings.home_leftcap_align !== 'left' && (
            <style>
              {`@media only screen and (min-width: 800px) {
                .capleft${block.id} {
                  text-align: ${settings.home_leftcap_align};
                }
              }`}
            </style>
          )}
          {settings.home_rightcap_align !==' right' && (
            <style>
              {`@media only screen and (min-width: 800px) {
                .capright{block.id} {
                  text-align: ${settings.home_rightcap_align};
                }
              }`}
            </style>
          )}
          <div className={jnstyls([styles.capleft, 'capleft' + block.id])}>
            {(settings.title && !settings.capimg_title) && (
              <>
                {(pdp && pdp_link) ? (
                  <Flink className={styles.title_link} href={pdp_link}>
                    <h2 className={jnstyls([bstyle.title, styles.title])} dangerouslySetInnerHTML={{ __html: settings.title }} />
                  </Flink>
                ) : (
                  <h2 className={jnstyls([bstyle.title, styles.title])} dangerouslySetInnerHTML={{ __html: settings.title }} />
                )}
              </>
            )}
            {(settings.title && settings.capimg_title) && (
              <ImgTitle 
                img_title={settings.capimg_title} 
                desk_height={settings.capimg_title_dheight}
                mobi_height={settings.capimg_title_mheight}
                title={{copy: settings.title, url: pdp_link}}
                klass={`capleft${block.id}img`}
              />
            )}
            {settings.subtitle && (
              <>
                {(pdp && pdp_link) ? (
                  <Flink className={styles.title_link} href={pdp_link}>
                    <div className={jnstyls([bstyle.subtitle])} dangerouslySetInnerHTML={{ __html: settings.subtitle }} />
                  </Flink>
                ) : (
                  <div className={jnstyls([bstyle.subtitle])} dangerouslySetInnerHTML={{ __html: settings.subtitle }} />
                )}
              </>
            )}
            {settings.copy && (
              <>
                {(didLoad && settings.copy?.includes('/products/') && quickProds.length > 0) ? (
                  <QuickCaption 
                    copy={settings.copy} 
                    styles={styles.copy_body}
                  />
                ) : (
                  <div className={styles.copy_body} dangerouslySetInnerHTML={{ __html: settings.copy}} />
                )}
              </>
            )}
            {settings.cta && (
              <div className={styles.copy_cta}>
                <Flink href={settings.cta_link} className={bstyle.cta_link}>
                  <span>{settings.cta}</span>
                </Flink>
              </div>
            )}
          </div>
          <div className={jnstyls([styles.capright, 'capright' + block.id])}>
            {settings.copy_right && (
              <>
                {(didLoad && settings.copy_right?.includes('/products/') && quickProds.length > 0) ? (
                  <QuickCaption 
                    copy={settings.copy_right} 
                    styles={styles.copy_body}
                  />
                ) : (
                  <div className={styles.copy_body} dangerouslySetInnerHTML={{ __html: settings.copy_right}} />
                )}
              </>
            )}
            {settings.cta_right && (
              <div className={styles.copy_cta}>
                <Flink href={settings.cta_link_right} className={bstyle.cta_link}>
                  <span>{settings.cta_right}</span>
                </Flink>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

const possible_png = (url) => {
  return imgSize(url,500)
}


const HeroImg = ({ settings, blogTitle }) => {
  const { isMobile } = useTheme()

  const [video, setVideo] = useState(null)

  let has_video = false
  let desk_vid = false
  let mobi_vid = false
  if (settings.hasOwnProperty('vidprod') && notBlank(settings.vidprod)) {
    desk_vid = settings.vidprod
  }
  if (settings.hasOwnProperty('mobi_vidprod') && notBlank(settings.mobi_vidprod)) {
    mobi_vid = settings.mobi_vidprod
  }
  has_video = desk_vid || mobi_vid

  let alt = settings.ov_title || settings.title || settings.subtitle || settings.caption || settings.link1_copy
  alt = alt?.replace(/<[^>]*>/g, '')
  if (!alt) alt = blogTitle || ''

  const image_url = () => {
    const desk_pres = notBlank(settings.img)
    const mobi_pres = notBlank(settings.img_mobile)
    if ((!isMobile && !desk_pres) || (isMobile && !(mobi_pres || desk_pres))) {
      return false
    }
    const desk_image = settings.img ? imgSize(settings.img,1800) : false
    const mobi_url = settings.img_mobile || settings.img
    let mobi_image = mobi_url ? imgSize(mobi_url,900) : false

    return !isMobile ? desk_image : mobi_image
  }

  useEffect(() => {
    if (!has_video) return
    const handle = !isMobile ? desk_vid : mobi_vid
    setVideo(handle)
  }, [isMobile, has_video, desk_vid, mobi_vid])

  return (
    <>
      {(!!has_video && video) && (
        <div className={styles.hero_vid}>
          <VideoNoControl 
            handle={video}
          />
        </div>
      )}
      {(!has_video && image_url()) && (
        <img 
          className={styles.img}
          src={image_url()}
          loading="lazy" 
          width={!isMobile ? 1800 : 900}
          height={!isMobile ? 1000 : 1000}
          alt={alt}
        />
      )}
    </>
  )
}

export default BlogHero



